import { Alert, AlertGroup, AlertVariant, AlertActionCloseButton, Button, Flex, FlexItem, FormGroup, Modal, Page, PageSection, Spinner, TextArea, ToolbarContent, ToolbarItem } from '@patternfly/react-core';
import React, { useEffect } from 'react';
import { transformData } from './FlowService';
import { getCurrentADMArchive } from './utils';

export function PreviewModal({ isModal, handleModalToggle, templateData }: { isModal: boolean, handleModalToggle: () => void, templateData: any }) {

    return (
        <React.Fragment>
            <Modal
                width={'75%'}
                title="Preview Data Transformation"
                isOpen={isModal}
                onClose={handleModalToggle}
                height={'50%'}
            >
                <Preview data={templateData} />
            </Modal>
        </React.Fragment>
    );
}

export function Preview({ data }: Readonly<{ data: any }>) {
    const [isLoading, setIsLoading] = React.useState(false);
    const [payload, setPayload] = React.useState('');
    const [result, setResult] = React.useState('');
    const [alerts, setAlerts] = React.useState<{ key: number, title: string, variant: AlertVariant }[]>([]);
    const alertKey = React.useRef(0);
    const [currentTemplate, setCurrentTemplate] = React.useState<any>(null);


    function addAlert(title: string, variant: AlertVariant) {
        setAlerts(prev => [...prev, { key: alertKey.current++, title, variant }]);
    }

    function removeAlert(key: number) {
        setAlerts(prev => prev.filter(alert => alert.key !== key));
    }

    function handlePreview() {
        setIsLoading(true);
        let parsedJson;
        try {
            parsedJson = JSON.parse(payload);
        }
        catch (e) {
            addAlert('Invalid JSON payload. Please enter valid JSON.', AlertVariant.danger);
            setIsLoading(false);
            return;
        }
        let formData = new FormData();
        if (currentTemplate) {
            formData.append('mappingTemplateFile', currentTemplate, data.templateName);
            formData.append('transformationRequestDTO', JSON.stringify({
                name: data.templateName,
                sourceId: data.sourceId,
                sourceEventType: data.sourceEventType,
                targetId: data.targetId,
                targetEventType: data.targetEventType,
                payloadData: parsedJson
            }
            ));
        }
        else {
            addAlert('No Template found to preview the transformation.', AlertVariant.warning);
            setIsLoading(false);
            return;
        }
        
        transformData(formData).then((response: any) => {
            setIsLoading(false);
            let data;
            if (response.data && response.data.dataContentType === 'XML') {
                let xmlDoc = new DOMParser().parseFromString(response.data.transformedData, 'application/xml');
                let xsltDoc = new DOMParser().parseFromString([
                    '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">',
                    '  <xsl:strip-space elements="*"/>',
                    '  <xsl:template match="para[content-style][not(text())]">',
                    '    <xsl:value-of select="normalize-space(.)"/>',
                    '  </xsl:template>',
                    '  <xsl:template match="node()|@*">',
                    '    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
                    '  </xsl:template>',
                    '  <xsl:output indent="yes"/>',
                    '</xsl:stylesheet>',
                ].join('\n'), 'application/xml');

                let xsltProcessor = new XSLTProcessor();
                xsltProcessor.importStylesheet(xsltDoc);
                let resultDoc = xsltProcessor.transformToDocument(xmlDoc);
                data = new XMLSerializer().serializeToString(resultDoc);
            }
            else {
                try {
                    data = JSON.stringify(response.data, undefined, 4);
                }
                catch (e) {
                    addAlert('Error parsing response: ' + (e instanceof Error ? e.message : e), AlertVariant.danger);
                    data = response.data;
                }
            }
            if (response.success) {
                setResult(data);
                addAlert(response.message ?? 'Transformation successful', AlertVariant.success);
            }
            else {
                setResult(response.errors == null ? response.message : response.errors);
                addAlert(response.errors == null ? response.message : 'Transformation failed', AlertVariant.danger);
            }
        }).catch((error: any) => {
            setIsLoading(false);
            addAlert(error?.message ?? 'An unexpected error occurred', AlertVariant.danger);
        });
    }

    useEffect(() => {
        getCurrentADMArchive().then(async (value: Uint8Array | null) => {
            if (value) {
                const fileContent = new Blob([value], {
                    type: 'application/octet-stream'
                });
                setCurrentTemplate(fileContent);
            }
            else {
                addAlert('No Template found to preview the transformation.', AlertVariant.warning);
            }
        });
    }, []);

    return (
        <React.Fragment>
            <AlertGroup isToast>
                {alerts.map(({ key, title, variant }) => (
                    <Alert
                        key={key}
                        variant={variant}
                        title={title}
                        timeout={4000}
                        onTimeout={() => removeAlert(key)}
                        isLiveRegion
                        actionClose={<AlertActionCloseButton title={title as string} onClose={() => removeAlert(key)} />}
                    />
                ))}
            </AlertGroup>
            {isLoading ? (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh'
        }}
    >
        <Spinner
            aria-label="Loading transformation preview"
            size="xl"
            style={{ color: '#0066cc' }}
        />
        <div style={{ fontSize: '1.1rem', color: '#0066cc', marginTop: '1rem' }}>
            Loading...
        </div>
    </div>
)  :
                <Page>
                    <PageSection padding={{ default: 'noPadding' }} variant='light'>
                        <Flex>
                            <FlexItem align={{ default: 'alignRight' }}>
                                <ToolbarItem alignment={{ default: 'alignRight' }} variant="search-filter">
                                    <ToolbarContent alignment={{ default: 'alignRight' }}>
                                        <Button onClick={() => { handlePreview(); }} isDisabled={!payload || payload.trim() === ''}>Test</Button>
                                    </ToolbarContent>
                                </ToolbarItem>
                            </FlexItem>
                        </Flex>
                    </PageSection>
                    <PageSection isFilled padding={{ default: 'noPadding' }} variant='light'>
                        <Flex>
                            <FlexItem style={{ flex: 'auto' }}>
                                <FormGroup label="Sample payload data" fieldId='sample-payload-textarea'>
                                    <TextArea label='Sample payload data' autoResize placeholder='Please input the sample payload here' style={{ height: '500px', width: '100%' }} value={payload} onChange={(value: any) => { setPayload(value) }} aria-label="text area example" />
                                </FormGroup>
                            </FlexItem>
                            <FlexItem style={{ flex: 'auto' }}>
                                <FormGroup label="Transformation response" fieldId='transformation-response-textarea'>
                                    <TextArea label='Transformation response' autoResize readOnly={true} placeholder='Transformation response' style={{ height: '500px', width: '100%' }} value={result} aria-label="text area example" />
                                </FormGroup>
                            </FlexItem>
                        </Flex>
                    </PageSection>
                </Page>}
        </React.Fragment>
    );
}
